/**
 * SIREN bundle (`siren_bundle`) submission rules.
 * Five HN columns. Every row, including Nutrition, is one 1/0/NA per HN.
 */
import { aggregateCompletenessStringRows } from './nursingComplianceScoring';
import { UI_HIDDEN_DEPARTMENT_IDS } from './departmentUi';

export const SIREN_HN_COUNT = 5;
/** SKIN 3 + Incontinence 3 + Relief 3 + Evaluation 2 + Nutrition 1. */
export const SIREN_COMPLIANCE_ITEM_COUNT = 12;

export const SIREN_NUTRITION_QUESTIONS = [
  '1. ผู้ป่วยมีน้ำหนักตัวลดลงใน 6 เดือน',
  '2. ผู้ป่วยทานอาหารได้น้อยลงกว่าที่เคย เกิน 7 วัน',
  '3. BMI <18.5 หรือ ≥ 25 กก/ตรม , หรือไม่',
  '4. ผู้ป่วยมีภาวะโรควิกฤต หรือโรคกึ่งวิกฤตร่วมด้วยหรือไม่',
] as const;

export const SIREN_NUTRITION_TOPIC =
  'มีการประเมินโภชนาการ โดยมีผลประเมินดังนี้ (*ถ้าใช่ = 1 คะแนน* , *ถ้าไม่ = 0 คะแนน*)';

/** One checklist row. Staff choose a single 1/0/NA per HN. The four lines are instructions. */
export const SIREN_NUTRITION_ITEM = [
  SIREN_NUTRITION_TOPIC,
  ...SIREN_NUTRITION_QUESTIONS,
  '***หากคะแนนมากกว่าเท่ากับ 2 ให้ปรึกษานักโภชนาการ หากน้อยกว่าให้คัดกรองซ้ำทุกสัปดาห์',
].join('\n');

/** Wards that do not submit SIREN bundle. OPD ids are matched by name, not listed one by one. */
export const SIREN_BUNDLE_EXCLUDED_DEPARTMENT_IDS = new Set<string>([
  ...UI_HIDDEN_DEPARTMENT_IDS,
  'ERD',
  'OR',
  'DR',
  'HD',
  'STROKE',
  'BLO_BNK',
  'ECC',
]);

export function isSirenBundleDepartmentExcluded(departmentId: string | null | undefined): boolean {
  if (!departmentId || typeof departmentId !== 'string') return false;
  const trimmed = departmentId.trim();
  if (!trimmed) return false;
  if (SIREN_BUNDLE_EXCLUDED_DEPARTMENT_IDS.has(trimmed)) return true;
  return trimmed.toUpperCase().includes('OPD');
}

/** Completeness of every SIREN cell, including the single Nutrition 1/0/NA. NA is left out. */
export function sirenCompliancePercent(answers: string[][]): number {
  return aggregateCompletenessStringRows(answers).pct;
}

const CHOICE = new Set(['1', '0', 'NA']);

const isNestedArrayWrapper = (value: unknown): value is { items: unknown[] } =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  Array.isArray((value as { items?: unknown }).items);

const cellToString = (cell: unknown): string => {
  if (cell == null) return '';
  if (typeof cell === 'string') return cell.trim();
  if (typeof cell === 'number' && Number.isFinite(cell)) return String(cell);
  return '';
};

const rowFromUnknown = (row: unknown): string[] => {
  if (isNestedArrayWrapper(row)) return row.items.map(cellToString);
  if (Array.isArray(row)) return row.map(cellToString);
  return [];
};

export function emptySirenAnswers(): string[][] {
  return Array.from({ length: SIREN_HN_COUNT }, () =>
    Array.from({ length: SIREN_COMPLIANCE_ITEM_COUNT }, () => ''),
  );
}

export function readSirenHn(source: unknown): string[] {
  const raw = Array.isArray(source) ? source : [];
  return Array.from({ length: SIREN_HN_COUNT }, (_, index) => cellToString(raw[index]));
}

export function readSirenAnswers(source: unknown): string[][] {
  const rows = Array.isArray(source) ? source : [];
  return Array.from({ length: SIREN_HN_COUNT }, (_, hnIndex) => {
    const row = rowFromUnknown(rows[hnIndex]);
    return Array.from({ length: SIREN_COMPLIANCE_ITEM_COUNT }, (_, itemIndex) => {
      const value = row[itemIndex] ?? '';
      return CHOICE.has(value) ? value : '';
    });
  });
}

/** One HN column counts when it was submitted, or every 1/0/NA cell in that column is chosen. */
export function sirenColumnCompletionFlags(input: {
  answers?: unknown;
  submitted?: unknown;
}): boolean[] {
  const answers = readSirenAnswers(input.answers);
  const submitted = Array.isArray(input.submitted) ? input.submitted : [];
  return Array.from({ length: SIREN_HN_COUNT }, (_, hnIndex) => {
    if (submitted[hnIndex] === true) return true;
    const row = answers[hnIndex] ?? [];
    for (let itemIndex = 0; itemIndex < SIREN_COMPLIANCE_ITEM_COUNT; itemIndex += 1) {
      if (!CHOICE.has(row[itemIndex] ?? '')) return false;
    }
    return true;
  });
}

/** Card percent: finished HN columns out of five. */
export function sirenCardProgressPercent(input: {
  answers?: unknown;
  submitted?: unknown;
}): number {
  const done = sirenColumnCompletionFlags(input).filter(Boolean).length;
  return Math.round((done / SIREN_HN_COUNT) * 100);
}

export function sirenGridIsComplete(input: {
  hn: readonly string[];
  answers: readonly (readonly string[])[];
}): boolean {
  if (input.hn.length < SIREN_HN_COUNT) return false;
  for (let hnIndex = 0; hnIndex < SIREN_HN_COUNT; hnIndex += 1) {
    if (!String(input.hn[hnIndex] ?? '').trim()) return false;
    const answers = input.answers[hnIndex] ?? [];
    for (let itemIndex = 0; itemIndex < SIREN_COMPLIANCE_ITEM_COUNT; itemIndex += 1) {
      if (!CHOICE.has(answers[itemIndex] ?? '')) return false;
    }
  }
  return true;
}
